/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import BrokenImageOutlinedIcon from "@mui/icons-material/BrokenImageOutlined";
import ContentShell from "components/ContentShell";
import CopyableTag from "components/CopyableTag";
import CustomButton from "components/CustomButton";
import DeleteConsentDialog from "components/DeleteConsentDialog";
import SearchInput from "components/SearchInput";
import Table from "components/Table";
import useConsent from "hooks/useConsent";
import { useSnackbar } from "hooks/useSnackbar";
import { defaultPageNo, defaultPageSize } from "utils/constants";
import {
  deletePackLibraryImage,
  listPackLibraryImages,
  replacePackLibraryImage,
  uploadPackLibraryImagesZip,
} from "services/packServices";

const useStyles = makeStyles(() => ({
  tableParent: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
  },
  preview: {
    width: 96,
    height: 132,
    objectFit: "contain",
    background: "repeating-conic-gradient(#eee 0% 25%, #fafafa 0% 50%) 50% / 14px 14px",
    border: "1px solid #e0e0e0",
    borderRadius: 6,
    padding: 3,
  },
  previewEmpty: {
    width: 96,
    height: 132,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px dashed #cfcfcf",
    borderRadius: 6,
    color: "#bdbdbd",
  },
}));

interface LibraryImage {
  _id: string;
  imageId: string;
  name?: string;
  imagePath?: string;
  width?: number;
  height?: number;
  imageUrl?: string;
}

// The items of one content type (pack, banner, ...) that still use an image.
interface ImageUsage {
  type: string;
  items: { id: string; name?: string }[];
}

const usageCount = ({ type, items }: ImageUsage) => `${items.length} ${type}${items.length === 1 ? "" : "s"}`;

// "2 packs and 1 banner", worded like the server's error message.
const usageSummary = (usages: ImageUsage[]) => {
  const counts = usages.map(usageCount);
  return counts.length > 1 ? `${counts.slice(0, -1).join(", ")} and ${counts[counts.length - 1]}` : counts[0] ?? "";
};

// Preview that degrades to a placeholder instead of a broken-image icon.
const ImageThumb = ({ url, label }: { url?: string; label: string }) => {
  const styles = useStyles();
  const [errored, setErrored] = useState(false);
  const busted = url ? `${url.split("?")[0]}?t=${Date.now()}` : url;

  return url && !errored ? (
    <img src={busted} alt={label} className={styles.preview} onError={() => setErrored(true)} />
  ) : (
    <Box className={styles.previewEmpty}>
      <BrokenImageOutlinedIcon fontSize="small" />
    </Box>
  );
};

// Compact "how it works" help shown in the toolbar info tooltip.
const HelpContent = () => (
  <Box sx={{ p: 0.5, lineHeight: 1.5 }}>
    <Typography variant="caption" sx={{ display: "block", fontWeight: 700, mb: 0.5 }}>
      How image IDs work
    </Typography>
    <Typography variant="caption" sx={{ display: "block" }}>
      Each image's <b>file name becomes its Image ID</b> (e.g. <code>winter-pack.png</code> → ID{" "}
      <code>winter-pack</code>). Re-uploading a ZIP <b>replaces</b> images whose ID already exists and{" "}
      <b>adds</b> the new ones, so the same ID is never duplicated. Reference an image by its ID in a
      bulk-upload XLSX, or pick it in the pack or banner editor.
    </Typography>
  </Box>
);

const ImageRepository = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState<LibraryImage[]>([]);
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [search, setSearch] = useState("");
  const [pageNo, setPageNo] = useState(defaultPageNo);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  const zipInputRef = useRef<HTMLInputElement | null>(null);
  const replaceInputRef = useRef<HTMLInputElement | null>(null);
  const replaceTargetId = useRef<string | null>(null);

  const { isOpenConsent, constentDetails, openConsent, closeConsent, updateDetails } = useConsent();

  // Shown when a delete is blocked because content still uses the image (game_be code 4011). The server
  // groups the usages by content type, so a new type shows up here without a change.
  const [inUse, setInUse] = useState<{ open: boolean; imageId: string; usages: ImageUsage[] }>({
    open: false,
    imageId: "",
    usages: [],
  });

  const fetchImages = async () => {
    try {
      setLoading(true);
      const resp = await listPackLibraryImages();
      setImages(resp?.data ?? []);
    } catch (error: any) {
      showSnackbar(error.message || "Failed to load images", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return images;
    return images.filter(
      (img) => img.imageId?.toLowerCase().includes(q) || img.name?.toLowerCase().includes(q),
    );
  }, [images, search]);

  const onZipSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    setZipFile(e.target.files?.[0] || null);
    e.target.value = "";
  };

  const onUploadZip = async () => {
    if (!zipFile) return;
    try {
      setLoading(true);
      await uploadPackLibraryImagesZip(zipFile);
      showSnackbar("Images uploaded successfully.", "success");
      setZipFile(null);
      await fetchImages();
    } catch (error: any) {
      showSnackbar(error.message || "Failed to upload images", "error");
    } finally {
      setLoading(false);
    }
  };

  const onReplaceClick = (imageId: string) => {
    replaceTargetId.current = imageId;
    replaceInputRef.current?.click();
  };

  const onReplaceFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    const imageId = replaceTargetId.current;
    e.target.value = "";
    if (!selected || !imageId) return;

    try {
      setLoading(true);
      await replacePackLibraryImage(imageId, selected);
      showSnackbar(`Image "${imageId}" replaced.`, "success");
      await fetchImages();
    } catch (error: any) {
      showSnackbar(error.message || "Failed to replace image", "error");
    } finally {
      setLoading(false);
    }
  };

  const openDeleteConsent = (image: LibraryImage) => {
    updateDetails({ itemName: image.imageId, id: image.imageId });
    openConsent();
  };

  const onConfirmDelete = async () => {
    const imageId = constentDetails.id;
    closeConsent();
    if (!imageId) return;
    try {
      setLoading(true);
      await deletePackLibraryImage(imageId);
      showSnackbar(`Image "${imageId}" deleted.`, "success");
      await fetchImages();
    } catch (error: any) {
      const details = error?.response?.data?.details;
      // 4011 = PACK_LIBRARY_IMAGE_IN_USE: list the blocking items in a modal so the IDs stay visible/copyable.
      if (details?.code === 4011 && Array.isArray(details?.metadata?.usages)) {
        setInUse({ open: true, imageId, usages: details.metadata.usages });
      } else {
        showSnackbar(
          typeof details === "string" ? details : error.message || "Failed to delete image",
          "error",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const copyAllIds = () => {
    navigator.clipboard.writeText(inUse.usages.flatMap((usage) => usage.items.map((item) => item.id)).join("\n"));
    showSnackbar("All IDs copied.", "success");
  };

  const columns = [
    {
      id: "preview",
      label: "Preview",
      isSortable: false,
      alignHead: "center",
      alignBody: "center",
      renderCell: (data: LibraryImage) => <ImageThumb url={data.imageUrl} label={data.imageId} />,
    },
    {
      id: "imageId",
      label: "Image ID",
      isSortable: false,
      alignHead: "center",
      alignBody: "center",
      renderCell: (data: LibraryImage) => <CopyableTag value={data.imageId} />,
    },
    {
      id: "name",
      label: "Name",
      isSortable: false,
      alignHead: "center",
      alignBody: "center",
      renderCell: (data: LibraryImage) => <CopyableTag value={data.name || data.imageId} />,
    },
    {
      id: "resolution",
      label: "Resolution",
      isSortable: false,
      alignHead: "center",
      alignBody: "center",
      renderCell: (data: LibraryImage) => (
        <Box sx={{ fontWeight: 600, fontSize: "13px" }}>
          {data.width && data.height ? `${data.width} × ${data.height}` : "—"}
        </Box>
      ),
    },
    {
      id: "imagePath",
      label: "Image Path",
      isSortable: false,
      alignHead: "center",
      alignBody: "center",
      renderCell: (data: LibraryImage) =>
        data.imagePath ? <CopyableTag value={data.imagePath} /> : <span>-</span>,
    },
    {
      // NOTE: not "actions" — the Table reserves that id for its Actions component and would ignore renderCell.
      id: "manage",
      label: "Actions",
      isSortable: false,
      alignHead: "center",
      alignBody: "center",
      renderCell: (data: LibraryImage) => (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: 0.75, minWidth: 130 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<SwapHorizIcon />}
            onClick={() => onReplaceClick(data.imageId)}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Replace
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => openDeleteConsent(data)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <ContentShell
      title="Image Repository"
      loading={loading}
      actions={
        <SearchInput
          value={search}
          placeholder="Search by Image ID or Name..."
          onSearch={(v: string) => {
            setSearch(v || "");
            setPageNo(defaultPageNo);
          }}
        />
      }
    >
      <input type="file" accept=".zip" hidden ref={zipInputRef} onChange={onZipSelected} />
      <input type="file" accept="image/*" hidden ref={replaceInputRef} onChange={onReplaceFileSelected} />

      {/* Compact upload toolbar — one line so the list gets the space */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          px: 1.5,
          py: 1,
          mb: 2,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
          background: "linear-gradient(135deg, #0891b20a 0%, transparent 60%)",
        }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<UploadFileIcon />}
          onClick={() => zipInputRef.current?.click()}
          sx={{ textTransform: "none", fontWeight: 600, whiteSpace: "nowrap" }}
        >
          Choose ZIP
        </Button>

        <Typography
          variant="body2"
          noWrap
          sx={{ maxWidth: 260, color: zipFile ? "text.primary" : "text.secondary", fontStyle: zipFile ? "normal" : "italic" }}
          title={zipFile?.name}
        >
          {zipFile ? zipFile.name : "No file selected"}
        </Typography>

        <CustomButton startIcon={<CloudUploadIcon />} disabled={!zipFile} onClick={onUploadZip}>
          Upload
        </CustomButton>

        <Box sx={{ flex: 1 }} />

        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
          PNG / JPG / WEBP · original resolution kept
        </Typography>
        <Tooltip arrow title={<HelpContent />} componentsProps={{ tooltip: { sx: { maxWidth: 320 } } }}>
          <IconButton size="small" sx={{ color: "text.secondary" }}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Paper>

      {/* List */}
      <Box className={styles.tableParent}>
        <Table
          columns={columns}
          data={filtered.slice((pageNo - 1) * pageSize, pageNo * pageSize)}
          currentPage={pageNo}
          pageSize={pageSize}
          totalEntries={filtered.length}
          highlightAltRow={true}
          disableMaxHeight={true}
          onChangePage={(p: number) => setPageNo(p)}
          onRowsPerPageChange={(e: any) => {
            setPageSize(Number(e?.target?.value ?? defaultPageSize));
            setPageNo(1);
          }}
        />
      </Box>

      <DeleteConsentDialog
        open={isOpenConsent}
        onClose={closeConsent}
        onConfirm={onConfirmDelete}
        itemName={constentDetails.itemName}
        itemDetails="If any content still uses this image, deletion will be blocked so nothing is left without an image."
      />

      {/* Image in use: every item that still uses it, grouped by content type, so the admin can reassign them */}
      <Dialog open={inUse.open} onClose={() => setInUse((s) => ({ ...s, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Image is in use</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <b>{inUse.imageId}</b> can't be deleted because it is still used by{" "}
            <b>{usageSummary(inUse.usages)}</b>. Change the image on these items first, then delete
            it. Click any ID to copy it.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxHeight: 320, overflowY: "auto" }}>
            {inUse.usages.map((usage) => (
              <Box key={usage.type} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: "capitalize" }}>
                  {usageCount(usage)}
                </Typography>
                {usage.items.map((item) => (
                  <Box
                    key={item.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1.5,
                      px: 1.5,
                      py: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1.5,
                    }}
                  >
                    <Typography variant="body2" noWrap sx={{ fontWeight: 600 }} title={item.name}>
                      {item.name || `(unnamed ${usage.type})`}
                    </Typography>
                    <CopyableTag value={item.id} />
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={copyAllIds} startIcon={<ContentCopyIcon />} sx={{ textTransform: "none" }}>
            Copy all IDs
          </Button>
          <Box sx={{ flex: 1 }} />
          <CustomButton onClick={() => setInUse((s) => ({ ...s, open: false }))}>Close</CustomButton>
        </DialogActions>
      </Dialog>
    </ContentShell>
  );
};

export default ImageRepository;
